import React, { useEffect, useState } from "react";
import { listSmsContacts, SmsContact } from "@/api/sms";
import { ActionConfig } from "@/types";
import { TextArea } from "@/components/primitives/TextArea";
import { SmsContactPicker } from "./SmsContactPicker";

export const SMS_VARIABLES = "{flow_name}, {step_name}, {url}, {method}, {expected_status}, {actual_status}, {error_message}, {response_body}, {started_at}";
export const DEFAULT_SMS_MESSAGE = "Failure: {flow_name} / {step_name}. Status: {actual_status}. {error_message}";

export const SmsActionFields: React.FC<{ actions: ActionConfig[]; onChange: (actions: ActionConfig[]) => void }> = ({ actions, onChange }) => {
  const index = actions.findIndex(a => a.action_type === "sms");
  const action = actions[index];
  const enabled = action?.is_active ?? false;
  const [contacts, setContacts] = useState<SmsContact[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    listSmsContacts().then(items => { if (!cancelled) { setContacts(items); setError(""); } }).catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load SMS contacts"); });
    return () => { cancelled = true; };
  }, [enabled]);
  const update = (changes: Partial<ActionConfig>) => {
    const next = [...actions];
    const base: ActionConfig = index >= 0 ? action : { name: "SMS failure alert", action_type: "sms", config: { message_template: DEFAULT_SMS_MESSAGE, contact_ids: [] }, is_active: false };
    const updated = { ...base, ...changes };
    if (index < 0) next.push(updated); else next[index] = updated;
    onChange(next);
  };
  const config = action?.config ?? { message_template: DEFAULT_SMS_MESSAGE, contact_ids: [] };
  return <div className="mb-4 space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
    <label className="flex items-center gap-3 text-sm font-medium text-slate-700 dark:text-slate-200"><input type="checkbox" checked={enabled} onChange={e => update({ is_active: e.target.checked })} />Send SMS on failure</label>
    {enabled && <>
      <p className="text-xs text-slate-500 dark:text-slate-400">Sent after all retries fail, alongside any other enabled actions. <a href="/sms" target="_blank" rel="noreferrer" className="underline">Manage SMS settings and contacts</a></p>
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      <SmsContactPicker contacts={contacts} selected={(config.contact_ids as string[]) ?? []} onChange={ids => update({ config: { ...config, contact_ids: ids } })} />
      <TextArea label="SMS failure message" required maxLength={2000} rows={3} value={(config.message_template as string) ?? ""} onChange={e => update({ config: { ...config, message_template: e.target.value } })} />
      <p className="text-xs text-slate-500 dark:text-slate-400">Available variables: {SMS_VARIABLES}. Use doubled braces for literal braces.</p>
    </>}
  </div>;
};
